/**
 * Base view entry point — the seam that ties the pure sim to the Three.js
 * renderer and DOM UI. `startBaseView(root)` mounts everything and returns a
 * teardown fn that fully disposes GPU + DOM + listeners (SPA nav remounts).
 *
 * Ownership: this file and `types.ts` + `data/` are the stable seam. The logic
 * agent works in `sim/`, the frontend agent in `render/` + `ui/`. Keep the
 * public surface (`startBaseView`) and the frame loop's calls into each side
 * unchanged unless the contract in `types.ts` changes.
 */
import * as THREE from 'three';
import { DEFAULT_CAMERA, type DialogNode, type ObjectId, type WorldData } from './types';
import { map as homeSettlement } from './data/maps/home_settlement';
import { MAP_REGISTRY } from './data/maps/registry';
import { ONE_SHOT_DEMO } from './data/maps/test_dummies';
import { createBaseSim, crouchSpeedMultiplier } from './sim/base-sim';
import { buildScene } from './render/scene';
import { attachWorldModels, swapObjectModel } from './render/model-attach';
import { homeLevelModelUrl } from './render/model-catalog';
import { buildZoneMesh } from './render/debug-zones';
import { buildDecalMesh } from './render/decal-mesh';
import { buildFogZoneMesh, updateFogDrift } from './render/fog-zone';
import { buildPlayerShadow } from './render/player-shadow';
import { createFloatingNumbers } from './render/floating-numbers';
import { createHealWatcher } from '../state/heal-feed';
import { medCenterSpawn } from './sim/respawn';
import { createPlayerAnimator } from './render/character-animations';
import { CameraController } from './render/camera-controller';
import { Picker } from './render/picking';
import { createMixer } from '../audio/mixer';
import { createFireAmbience } from '../audio/fire-ambience';
import { createLevelUpSound } from '../audio/level-up-sound';
import { createRaidStartSound } from '../audio/raid-start-sound';
import { createDamageSound } from '../audio/damage-sound';
import { createUiSounds } from '../audio/ui-sounds';
import { TRACKS } from '../audio/tracks';
import { createPlayerControls } from './player-controls';
import { keyName, trackHeldKeys } from './key-input';
import { DebugOverlay } from './ui/debug-overlay';
import { createTutorialUI } from './ui/tutorial-ui';
import { DialogUI } from './ui/dialog-ui';
import { Hud } from './ui/hud';
import { Minimap } from './ui/minimap';
import { Paperdoll } from './ui/paperdoll';
import {
  createWorldMapUI, openWorldMapUI, hideWorldMapUI, isWorldMapUIOpen,
  disposeWorldMapUI, updateRaidPolling,
} from './ui/world-map-ui';
import { loadCurrentRaid, raidLocksWorld, raidStateStore } from '../state/raid-state';
import { isSettingsUIOpen } from '../settings/settings-ui';
import { createVendorUI, openVendorUI, disposeVendorUI } from './ui/vendor-ui';
import { createCurrencyShopUI, disposeCurrencyShopUI } from './ui/currency-shop-ui';
import { createBankerUI, openBankerUI, disposeBankerUI } from './ui/banker-ui';
import {
  createShopUI, openShopUI, closeShopUI, disposeShopUI, isShopUIOpen,
} from './ui/shop-ui';
import {
  createQuestUI, openQuestUI, closeQuestUI, disposeQuestUI, isQuestUIOpen,
} from './ui/quest-ui';
import { createQuestMarkers } from './render/quest-markers';
import { createNameTags, playerDisplayName } from './render/name-tags';
import { bindActionWindows, createActionBar } from './ui/action-bar';
import { createArenaUI, isArenaModalOpen } from './ui/arena-ui';
import { createChatUI, isChatOpen, isTypingTarget, toggleChatUI } from './ui/chat-ui';
import {
  closeHomeUI, createHomeUI, disposeHomeUI, HOME_NPC_ID, isHomeUIOpen, openHomeUI,
} from './ui/home-ui';
import {
  arenaStateStore, openArenaConfirm,
} from '../state/arena-state';
import { connectChat } from '../state/chat-state';
import { questStateStore, loadQuestBoard, loadJobBoard } from '../state/quest-state';
import { activeQuestCount } from '../state/quest-tracker';
import { createQuestTrackerUI } from './ui/quest-tracker-ui';
import { offerWalkthrough } from '../state/tutorial-state';
import { injectStyles } from './ui/styles';
import {
  closeOtherModals, minimapBlocked, modalOpen, registerPanel, worldInputBlocked,
} from './ui/panels';
import {
  chooseReply, closeDialog as closeNpcDialog, loadNpcs, openDialog as openNpcDialog,
  type DialogNodeView,
} from '../state/npc-state';
import { gameStateStore } from '../state/game-state';
import { homeStateStore, loadHome } from '../state/home-state';
import { authStore } from '../../core/state/auth-store';

/**
 * Which map to load. `home_settlement` unless a DEV build is asked for another
 * by `?map=<mapKey>` — the seam `registry.ts` was written for and has been
 * waiting on ("this registry does not yet drive map selection").
 *
 * **DEV only, deliberately.** `test_dummies` is an inspection rig, not a place
 * in the game; and letting a query string pick the world in production would
 * let anyone load a map the game's own progression never routes to. In a prod
 * build this compiles down to the static import and nothing else.
 */
function resolveWorld(): { data: WorldData; mapKey: string } {
  const fallback = { data: homeSettlement, mapKey: 'home_settlement' };
  if (!import.meta.env.DEV) return fallback;
  const key = new URLSearchParams(window.location.search).get('map');
  if (!key) return fallback;
  const entry = MAP_REGISTRY.find((m) => m.mapKey === key);
  if (!entry) {
    console.warn(`[base-view] ?map=${key} is not in MAP_REGISTRY — loading home_settlement. `
      + `Known: ${MAP_REGISTRY.map((m) => m.mapKey).join(', ')}`);
    return fallback;
  }
  return { data: entry.data, mapKey: entry.mapKey };
}

export function startBaseView(
  root: HTMLElement,
  playerModel?: THREE.Object3D,
  /** S17 — fired after `locationStateStore.active` is populated by a successful Enter. Swapping to location-view is bootstrap.ts's job, not this module's — it already owns the mount/teardown lifecycle. */
  onEnterLocation?: () => void,
): () => void {
  const cleanups: Array<() => void> = [];
  const removeStyles = injectStyles();
  let torndown = false;
  const { data: world, mapKey } = resolveWorld();
  // `test_dummies`' one-shot scaffolding — see that map's ONE_SHOT_DEMO. Empty
  // for every other map, so the frame-loop cost is one `.length === 0` check.
  const oneShots = mapKey === 'test_dummies' ? ONE_SHOT_DEMO : [];
  const oneShotTimers = new Map<string, number>();

  // ---- container + renderer ----
  root.innerHTML = '';
  const container = document.createElement('div');
  container.className = 'bv-root';
  root.appendChild(container);

  const renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.setSize(container.clientWidth || 1, container.clientHeight || 1);
  // The resize handler below passes `updateStyle: false`, so the pixel size
  // this call wrote into the canvas's inline style would otherwise stick: a
  // rotated phone kept a landscape-sized canvas and showed a stretched crop.
  renderer.domElement.style.width = '100%';
  renderer.domElement.style.height = '100%';
  container.appendChild(renderer.domElement);

  // ---- world / sim / render ----
  // ---- spawn: the front gate, or the Medbay if you were carried there ----
  //
  // `atMedCenter` is set by `HealthService` when HP hits 0 and cleared when the
  // player heals back to full. It is fresh here: `health:downed` triggers a
  // full `loadGameState()` (see `connectGameStateSync`), so the sheet has
  // already been refetched by the time the settlement remounts.
  //
  // Read ONCE, at mount, deliberately. This is where the player wakes up, not a
  // rule about where they may stand — reacting to the flag later would teleport
  // someone who happened to heal up while walking around.
  const health = gameStateStore.get().health;
  const wokeAtMedCenter = health?.state === 'ACTIVE' && health.atMedCenter;
  const respawnPoint = wokeAtMedCenter ? medCenterSpawn(world) : null;
  const sim = createBaseSim(world, respawnPoint ? { spawn: respawnPoint } : undefined);

  // ---- live character-stat -> sim speed bridge (character-animation-runtime.md
  // §3.4/§3.7 "Not yet done" follow-up) ----
  // Equipment loads async and can change post-mount (gear swap), so this can't
  // be a one-time value read at sim construction. `gameStateStore.subscribe`
  // fires immediately with whatever's in the store right now AND again on
  // every later push (same pattern hud.ts/paperdoll.ts already use), so this
  // single subscription covers "not loaded yet at mount" (no-ops via the
  // guard below, sim keeps its construction-time placeholder), "load finishes
  // a moment later", and "gear swap changes blendedStats.runSpeed" uniformly
  // — no separate initial call needed.
  const unsubStatSpeeds = gameStateStore.subscribe(() => {
    const { character, equipment } = gameStateStore.get();
    if (character?.state !== 'ACTIVE' || !equipment) return; // not loaded yet
    sim.setStatSpeeds(
      equipment.blendedStats.runSpeed,
      crouchSpeedMultiplier(character.attributes.agility, character.attributes.perception),
    );
  });
  cleanups.push(unsubStatSpeeds);

  const sceneHandles = buildScene(world, playerModel);
  // ---- player animation runtime (character-animation-runtime.md Phase 1) ----
  // No-ops itself if `sceneHandles.player` has no SkinnedMesh (the
  // buildHumanoid() fallback primitive) — safe to create/update/dispose
  // unconditionally. Self-contained: subscribes to gameStateStore (equipment)
  // internally, so no store-subscription code is needed here.
  const playerAnimator = createPlayerAnimator(sceneHandles.player);
  // Inspection maps hide the player — see `WorldData.hidePlayerModel`. The
  // animator is still created and updated: it is what would break if hiding
  // ever moved earlier in the pipeline, and a debug map should exercise the
  // normal path, not a special one.
  if (world.hidePlayerModel) sceneHandles.player.visible = false;
  cleanups.push(() => playerAnimator.dispose());
  // Per-map camera overrides merged over the shipped defaults — only
  // `test_dummies` sets any (it needs a 1m floor to inspect a garment seam).
  const cam = new CameraController(aspectOf(container), { ...DEFAULT_CAMERA, ...world.camera });
  // ---- audio: the settlement bed + a crackle at every authored fire ----
  //
  // Wrapped because an AudioContext is not guaranteed to exist: jsdom has
  // none, and a browser may refuse to allocate one when too many are already
  // open. Losing sound must not take the world view down with it, so every
  // failure here degrades to a silent game rather than a blank screen.
  //
  // Music is gated on the map. `startBaseView` also serves the arenas and the
  // raid maps, and the settlement bed playing over a firefight would be
  // wrong; the two PvP scores that belong there are unwired for the reason
  // documented on `TRACKS.pvpStealth`. Fire ambience is NOT gated — it is
  // driven by whatever fires the map itself authored, which is exactly as
  // true of the scrapyard as of home.
  // Reused across frames — a fresh Vector3 sixty times a second is garbage
  // the collector has to chase for no reason.
  const listenerForward = new THREE.Vector3();
  let fireAmbience: ReturnType<typeof createFireAmbience> | null = null;
  try {
    const mixer = createMixer();
    cleanups.push(() => mixer.dispose());
    fireAmbience = createFireAmbience(mixer);
    fireAmbience.setSpawns(world.spriteSpawns);
    cleanups.push(() => fireAmbience?.dispose());
    // Not gated by map — levelling up is worth the same stinger wherever the
    // player happens to be standing when it lands.
    cleanups.push(createLevelUpSound(mixer));
    cleanups.push(createRaidStartSound(mixer));
    // Clicks, ka-ching, purchases and errors. All four are delegated or
    // store-driven rather than wired per call site — see the module header.
    cleanups.push(createUiSounds(mixer));
    // Taking a hit. Not gated by map either: it watches the raid timeline,
    // which advances while the player is standing in the settlement looking at
    // the world map — that IS where a raid is watched from.
    cleanups.push(createDamageSound(mixer));
    if (mapKey === 'home_settlement') {
      // `playLoop` resolves once the clip has decoded; the stop function it
      // returns has to survive teardown that happens before then, hence the
      // promise-chained cleanup rather than an awaited one.
      let stopMusic: (() => void) | null = null;
      let tornDownEarly = false;
      void mixer.playLoop(TRACKS.settlementAmbient.path, 'music', 1, 3)
        .then((stop) => { if (tornDownEarly) stop(); else stopMusic = stop; })
        .catch(() => { /* a missing score is not worth a broken settlement */ });
      cleanups.push(() => { tornDownEarly = true; stopMusic?.(); });
    }
  } catch {
    // No Web Audio here. Everything below checks `fireAmbience` for null.
  }

  const picker = new Picker();
  const reducedMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');
  const interactableIds = new Set(world.objects.filter((o) => o.interactable).map((o) => o.id));

  // ---- NPC bodies + world object models: real GLBs, shared with the editor ----
  // See render/model-attach.ts for the disposal-safety/torndown handling.
  const npcAnimators = attachWorldModels(world, sceneHandles, () => torndown);
  cleanups.push(() => npcAnimators.dispose());

  // ---- the player's home shows the level they actually paid for ----
  //
  // The map is baked with the LARGEST mesh (`homeLevel: 6`), because that is
  // the one whose footprint had to clear its neighbours. The server knows the
  // real level, and it arrives after mount — so this swaps the GLB on the live
  // scene node and leaves `size`/`collisionW`/`collisionD` exactly as placed:
  // the plot stays reserved at full size whatever is standing on it.
  //
  // Driven by a store subscription rather than a one-shot read, so upgrading
  // from inside the Home panel rebuilds the house without a reload.
  const homeObject = world.objects.find((o) => o.npcId === HOME_NPC_ID);
  let shownHomeLevel = homeObject?.homeLevel ?? null;
  cleanups.push(homeStateStore.subscribe((state) => {
    const level = state.home?.level;
    const homeRoot = homeObject && sceneHandles.objectMeshes.get(homeObject.id);
    // `shownHomeLevel` starts at what the map baked, so the subscription's
    // immediate first fire cannot re-load a mesh that is already on screen.
    if (!homeObject || !homeRoot || !level || level === shownHomeLevel) return;
    shownHomeLevel = level;
    void swapObjectModel(
      homeRoot,
      homeLevelModelUrl(level),
      homeObject.modelHeight ?? homeObject.size.h,
      homeObject.color,
      () => torndown,
    ).catch((err) => console.error('[base-view] failed to swap the home model', err));
  }));
  // ---- the house is not yours until the deed is ----
  //
  // Janeks: *"before it is the player's = cannot open it, it stays on the map
  // as an abandoned house. The key/quest unlocks it for opening, view and
  // etc."* Until 2026-09-16 the panel opened regardless and merely printed
  // "Not yours yet" over a working crafting bench.
  //
  // FAILS CLOSED. `state.home` is null before `loadHome()` answers, and null
  // reads as unowned — the same asymmetry `HOME_GRANT_QUEST_SLUG` documents on
  // the server: degrading to "anyone may walk in" would hand out for free the
  // thing the Gold home key is priced at.
  //
  // The server holds the same line (`ZoneService.requireZone` throws
  // `HOME_NOT_OWNED`), so this is the visible half of the rule, not the whole
  // of it.
  let homeDeedHeld = false;
  cleanups.push(homeStateStore.subscribe((state) => {
    homeDeedHeld = state.home?.owned ?? false;
    if (homeObject) sim.setInteractableSuppressed(homeObject.id, !homeDeedHeld);
  }));

  // Fetched at mount, not left until the panel is first opened: otherwise the
  // settlement shows the baked level-6 mansion to a player living in a shack
  // until they happen to press H. The panel reads the same store, so this is
  // the load it would have made anyway.
  void loadHome();

  // ---- debug-only visuals: collisionZones have no rendering path otherwise ----
  // (see render/debug-zones.ts — same builder ultra_map_editor uses, so what
  // DEBUG shows here is exactly the box base-sim.ts's collision math uses).
  // Built once, up front, and just toggled visible/invisible — zones are
  // static, no need to rebuild on every DEBUG toggle.
  const debugZonesGroup = new THREE.Group();
  debugZonesGroup.visible = false;
  for (const zone of world.collisionZones ?? []) {
    debugZonesGroup.add(buildZoneMesh(zone));
  }
  sceneHandles.scene.add(debugZonesGroup);

  // ---- decals: real ground detail, always visible (not a debug aid) ----
  for (const decal of world.decals ?? []) {
    sceneHandles.scene.add(buildDecalMesh(decal));
  }

  // ---- zone fog: real ambient detail, always visible — see fog-zone.ts ----
  for (const zone of world.collisionZones ?? []) {
    const fogMesh = buildFogZoneMesh(zone);
    if (fogMesh) sceneHandles.scene.add(fogMesh);
  }

  // ---- no combat here ----
  //
  // The settlement used to carry the whole firing apparatus: a muzzle-flash
  // pool, a gunshot/swing sound, an aim arc and an LMB/Space attack path —
  // all of it hung off `controls.aiming`, and all of it feeding an
  // `attemptAttack()` that was a documented no-op. Janeks, 2026-09-19: *"we
  // need to disable aiming and shooting at home settlement."*
  //
  // Removed rather than gated. `createPlayerControls({ allowAim: false })`
  // below is now the single switch, and there is nothing left downstream of
  // it to keep in step: a flash with no shot, a crack with no target and a
  // weapon-ready stance in a safe zone were each promising combat this view
  // does not have. The machinery all still ships — `arena-view.ts` is where
  // it belongs, and the duel uses every part of it.

  // ---- player shadow: always follows, repositioned in the frame loop below ----
  const playerShadow = buildPlayerShadow();
  sceneHandles.scene.add(playerShadow);

  // ---- floating heal numbers ----
  //
  // Heals only, deliberately: the settlement is a safe zone with no combat in
  // it, so there is no damage to report. What there IS is passive regen, which
  // is lazy — nothing ticks it and no event announces it, so it is derived from
  // the health sheet climbing between two `health:updated` pushes. See
  // `state/heal-feed.ts` for the several things that look like a heal and are
  // not (a bigger max HP, a Med-Center respawn).
  //
  // The HP/armour BARS are not built here. Janeks: "only in battle mode, not in
  // home safezone settlement."
  const healNumbers = createFloatingNumbers();
  sceneHandles.scene.add(healNumbers.object);
  cleanups.push(() => healNumbers.dispose());
  const healWatcher = createHealWatcher();
  cleanups.push(gameStateStore.subscribe((s) => {
    const healed = healWatcher(s.health);
    if (!healed) return;
    const at = sim.getSnapshot().player.position;
    healNumbers.spawn({
      text: `+${Math.round(healed.amount)}`,
      tone: 'heal',
      emphasis: false,
      x: at.x,
      y: MUZZLE_HEIGHT,
      z: at.z,
    });
  }));


  // ---- UI ----
  // The fps/hover/pos readout and its DEBUG toggle are a development aid and
  // must never ship: it sat in the lower-left corner of the production build,
  // over the game, offering a button that reveals `collisionZones`.
  //
  // Gated at CONSTRUCTION rather than hidden with CSS, so the class and its
  // per-frame `update` are dead code Vite's tree-shaker drops from the prod
  // bundle entirely — hiding it would still ship the markup, still run the
  // four `textContent` writes every frame, and still leave the toggle one
  // devtools line away. Same shape as `resolveWorld`'s guard above and
  // `minimap.ts`'s `__bakeMinimap`.
  //
  // Null in a prod build, hence the `?.` at the two call sites below. Enforced
  // by `debug-overlay.prod.test.ts`, which reads THIS FILE: `import.meta.env`
  // cannot be flipped per-test, so the guard is checked in the source.
  const debug = import.meta.env.DEV
    ? new DebugOverlay(container, (enabled) => {
      debugZonesGroup.visible = enabled;
    })
    : null;
  const dialog = new DialogUI(container);
  const hud = new Hud(container);
  const paperdoll = new Paperdoll(container);
  // Derived from the same `world` the scene and sim use — no second source of
  // truth for the settlement's layout. See render/minimap-draw.ts.
  const minimap = new Minimap(container, world);
  // ONE raid surface, opened by either M or R.
  //
  // There used to be two — a compact panel (R) and the map (M) — which was
  // redundant and actively broken: the map wrote region data to its own store
  // while the panel's launch button read a *different* store's `selectedRegion`,
  // so launching from the map silently did nothing. Merged rather than patched;
  // duplicated state for one concept is what caused the bug.
  const worldMapUI = createWorldMapUI(onEnterLocation ?? (() => {}));
  container.appendChild(worldMapUI);
  const vendorUI = createVendorUI();
  container.appendChild(vendorUI);
  const currencyShopUI = createCurrencyShopUI();
  container.appendChild(currencyShopUI);
  const bankerUI = createBankerUI();
  container.appendChild(bankerUI);
  const questUI = createQuestUI();
  container.appendChild(questUI);
  const shopUI = createShopUI();
  container.appendChild(shopUI);
  const homeUI = createHomeUI();
  container.appendChild(homeUI);
  // ---- raid mode ----
  //
  // While a raid is running the player is not in the settlement, so the
  // settlement is not drawn: Janeks, 2026-10-13: *"While in raid - hide the
  // background - the player is raiding, he is not in the city - no world
  // behind, all we see is the map."* Driven from the raid STORE, not from
  // whether the map happens to be open, because the map can be closed by
  // another window asking for the screen and the city must not come back
  // through the gap.
  //
  // The canvas is hidden and the 3D render is skipped (the frame loop keeps
  // running — `updateRaidPolling()` lives in it). The container behind it is a
  // flat ground colour, so there is nothing left to see.
  let inRaid = raidLocksWorld();
  const applyRaidMode = (): void => {
    const next = raidLocksWorld();
    if (next === inRaid) return;
    inRaid = next;
    renderer.domElement.style.visibility = inRaid ? 'hidden' : '';
    // Rising edge: put the player on the raid screen. Falling edge: they have
    // pressed Continue, so take it down — `hideWorldMapUI`, not the guarded
    // request, because the guard exists to stop exactly everything but this.
    if (inRaid) openWorldMapUI(); else hideWorldMapUI();
  };
  renderer.domElement.style.visibility = inRaid ? 'hidden' : '';
  cleanups.push(raidStateStore.subscribe(applyRaidMode));
  // Restore a raid that was already running when the page loaded; see
  // `loadCurrentRaid`. The subscription above does the rest.
  void loadCurrentRaid().then(() => { if (raidLocksWorld()) openWorldMapUI(); });

  /**
   * Does anything take the world's own input right now — movement, camera,
   * zoom, click-to-interact?
   *
   * The registry's answer plus the three surfaces that live outside it: the
   * arena's accept/lobby modal (tracked on its own store), the Settings screen
   * (mounted by bootstrap, over everything) and the raid screen itself.
   */
  const worldBlocked = (): boolean =>
    worldInputBlocked() || isArenaModalOpen() || isSettingsUIOpen() || inRaid;

  // ---- action bar: one button per gameplay window, under the player frame ----
  //
  // This list is the SINGLE definition of each window's shortcut, its gate and
  // its toggle. The bar renders from it and `onKeyDown` below matches against
  // it, so a button can never do something different from its key — which is
  // what the five hand-copied `!dialog.isOpen && !isVendorUIOpen() && …`
  // chains this replaces were always one edit away from.
  //
  // The gates are deliberately NOT uniform: they reproduce exactly what each
  // binding did before. The player record opens over a vendor panel (it is a
  // reference you want while trading); the other three do not. Making them
  // agree would be a behaviour change nobody asked for.
  // The conversation and the Survivor Record are the two screen-taking
  // surfaces this file OWNS, so this file registers them; every other panel
  // registers itself from its own module (see ui/panels.ts).
  cleanups.push(registerPanel({
    id: 'dialog', isOpen: () => dialog.isOpen, close: () => closeDialog(),
  }));
  cleanups.push(registerPanel({
    // The Record used to register `blocksWorldInput: false` — it was the one
    // window that let the survivor keep walking and the camera keep zooming
    // underneath it. Janeks, 2026-10-13: *"Disable zoom and movement when any
    // of the menus are open."* Any means this one too, so it takes the default.
    id: 'record',
    isOpen: () => paperdoll.open,
    close: () => paperdoll.hide(),
  }));
  //
  // ─── What gates a window button, since 2026-09-18 ─────────────────────────
  // It used to be "another panel owns the screen" — every button went DISABLED
  // while any window was open. Janeks asked for one window at a time, and the
  // registry now enforces that at the point of opening (`closeOtherModals`), so
  // a button that yields to its sibling is better than one that goes dead: you
  // press Journal, you get the Journal. The gates below therefore only carry
  // what is genuinely a reason NOT to open at all.
  //
  // `isArenaModalOpen` is the one blanket block, and it is deliberately NOT
  // exemptible: an accept prompt with a 20-second fuse, or a freeze nobody can
  // act during, must outrank every window including the arena's own button. It
  // is checked outside the registry rather than registered with an id
  // something could pass as an exemption.
  //
  // WHAT each button is (name, key, icon) lives in `ACTION_WINDOWS`, where a
  // test can read it. This supplies only what each one DOES — which has to be
  // here, because this is the file that holds the panels.
  const actionItems = bindActionWindows({
    inventory: {
      isOpen: () => paperdoll.open,
      toggle: () => paperdoll.toggle(),
      enabled: () => !isArenaModalOpen() && !inRaid,
    },
    journal: {
      isOpen: isQuestUIOpen,
      toggle: () => (isQuestUIOpen() ? closeQuestUI() : openQuestUI()),
      enabled: () => !isArenaModalOpen() && !inRaid,
      // Same rows the side tracker lists, so the two can never disagree.
      badge: () => activeQuestCount(questStateStore.get()),
    },
    home: {
      isOpen: isHomeUIOpen,
      toggle: () => (isHomeUIOpen() ? closeHomeUI() : openHomeUI()),
      // The deed gates the BUTTON and the H key as well as the front door.
      // They open the same panel by a different route, so leaving them
      // ungated would have left the whole fix reachable in one keystroke.
      enabled: () => homeDeedHeld && !isArenaModalOpen() && !inRaid,
    },
    shop: {
      // Was bound to B ("black market"); moved to P at Janeks' request. No
      // other binding uses P — checked against this file (WASD/arrows, E, I,
      // J, M, R) and player-controls.ts (RMB, CapsLock, C).
      isOpen: isShopUIOpen,
      toggle: () => (isShopUIOpen() ? closeShopUI() : openShopUI()),
      enabled: () => !isArenaModalOpen() && !inRaid,
    },
    map: {
      isOpen: isWorldMapUIOpen,
      toggle: () => (isWorldMapUIOpen() ? hideWorldMapUI() : openWorldMapUI()),
      enabled: () => !isArenaModalOpen() && !inRaid,
    },
    arena: {
      // The button opens the confirm; it never cancels a queue. Leaving the
      // queue is the badge's own ✕, which is reachable with any panel open —
      // the button is not, once a modal phase owns the screen.
      isOpen: () => arenaStateStore.get().confirmOpen,
      toggle: () => openArenaConfirm(),
      // Only from a standing start. Pressing Arena while already queued or
      // mid-lobby has no sensible meaning, and the server would reject it
      // anyway (ALREADY_QUEUED / ALREADY_IN_MATCH).
      enabled: () => arenaStateStore.get().view.phase === 'IDLE' && !isArenaModalOpen() && !inRaid,
    },
    chat: {
      // Chat is a corner box, not a screen — it stays usable over an open
      // panel, which is the whole reason it is not one. Only a conversation
      // takes the keyboard away from it.
      isOpen: isChatOpen,
      toggle: () => toggleChatUI(),
      enabled: () => !dialog.isOpen && !isArenaModalOpen(),
    },
  });
  // Its own dock in the bottom-left corner, not a slot in the player frame —
  // see ui/action-bar.ts's header.
  const actionBar = createActionBar(actionItems);
  container.appendChild(actionBar.el);
  cleanups.push(() => actionBar.dispose());

  // ---- arena + world chat ----
  // The pending badge goes into the HUD column (above the panel scrims, so it
  // survives an open inventory); the modal layer and the chat box are siblings
  // of the canvas.
  const arenaUI = createArenaUI();
  hud.arenaStatusHost.appendChild(arenaUI.statusEl);
  container.appendChild(arenaUI.modalEl);
  cleanups.push(() => arenaUI.dispose());

  const chatUI = createChatUI();
  container.appendChild(chatUI.el);
  cleanups.push(() => chatUI.dispose());

  // The arena socket is NOT wired here: `bootstrap.ts` owns it, because it
  // has to survive this view being torn down for the arena (see its note).
  cleanups.push(connectChat());

  // ─── Reporting "map loaded" now belongs to the ARENA ────────────────────
  // It used to happen here, on entering LOBBY, with a comment explaining that
  // there was no scene to wait on because the client did not render the arena
  // yet. `arena-view.ts` does render it, and reporting from the settlement
  // meant the server started the eight-second hold while the player was still
  // looking at the lobby modal — the map then loaded after the countdown it
  // was meant to load during. Janeks: *"the countdown of 'no-one is moving'
  // is when the pvp map is already loaded. not on the matchmaking ui."*
  //
  // `bootstrap.ts` now swaps to the arena at LOBBY and the report fires from
  // that view's first rendered frame. Nothing here replaces it.

  /**
   * The guided walkthrough, and the offer of it to a first-time player.
   *
   * Mounted here rather than in `bootstrap.ts` next to the character chooser,
   * even though "after first login" is when it fires: five of the eight steps
   * ring an action-bar button, and the action bar does not exist until this
   * function has built it. Offering the tour from the creation branch would
   * point at a bar that is still three awaits away.
   *
   * `offerWalkthrough` is a no-op for anyone who has been through it and for
   * anyone who is mid-tour, so calling it on EVERY settlement mount — which
   * includes every return from a Location visit — neither nags nor interrupts.
   * See its docstring.
   */
  const tutorial = createTutorialUI(container);
  cleanups.push(() => tutorial.dispose());
  offerWalkthrough();

  // NPC catalog is static content — fetch once on entering the settlement.
  // Dialog text itself is fetched per conversation, not cached here.
  void loadNpcs();

  // ---- quest markers: the floating !/? over NPCs and their buildings ----
  // Driven by the SAME store the journal panel reads, so a quest accepted in
  // conversation, from the panel, or by any other path redraws the glyphs with
  // no second fetch and no chance of the two disagreeing. Fetched once on
  // entry so the markers are up before the player opens anything.
  const questMarkers = createQuestMarkers(world);
  sceneHandles.scene.add(questMarkers.object);
  const unsubQuestMarkers = questStateStore.subscribe(() => {
    questMarkers.setBoard(questStateStore.get().board);
    minimap.setPins(questMarkers.targets.map((t) => t.position));
  });
  cleanups.push(unsubQuestMarkers, () => questMarkers.dispose());
  void loadQuestBoard();
  // The side tracker and the Journal badge count the day's jobs too, so they
  // are read on entry rather than only when the journal is first opened.
  void loadJobBoard();
  const questTracker = createQuestTrackerUI(container, () => {
    if (!isQuestUIOpen() && !isArenaModalOpen() && !inRaid) openQuestUI();
  });
  cleanups.push(() => questTracker.dispose());

  // ---- name tags: who everyone is, floating over their heads ----
  // Built once from the world (the set of placed bodies is static) and then
  // only moved; the player's own label comes from the auth store, which is
  // already populated by the time the settlement mounts but is subscribed to
  // rather than read once so a later push (SPA sign-out -> sign-in reuses this
  // module) cannot leave the previous account's name over the new player.
  const nameTags = createNameTags(world);
  sceneHandles.scene.add(nameTags.object);
  const unsubNameTags = authStore.subscribe(() => {
    // `null` (its documented "removes it") clears the player's own tag — on an
    // inspection map the body is hidden, and a floating green name over
    // nothing is just noise.
    nameTags.setPlayerName(world.hidePlayerModel ? null : playerDisplayName(authStore.get().user));
  });
  cleanups.push(unsubNameTags, () => nameTags.dispose());

  const prompt = document.createElement('div');
  prompt.className = 'bv-prompt';
  prompt.hidden = true;
  container.appendChild(prompt);

  // ---- input state ----
  const keys = new Set<string>();
  let hoverId: ObjectId | null = null;
  let promptNpcId: string | null = null;
  let dragging = false;
  let dragMoved = 0;
  let lastPointerX = 0;
  // Desktop control scheme (character-animation-runtime.md §3.7): RMB-held
  // aim, Capslock-toggled gait, 'c'-toggled crouch. Owned by
  // `player-controls.ts` and shared with the location view, which otherwise
  // had no access to any of it — see that module's header. Feeds InputState
  // each frame below, independent of `keys` (which only tracks WASD/arrows).
  const controls = createPlayerControls({
    container,
    canvas: renderer.domElement,
    picker,
    getCamera: () => cam.camera,
    isInputBlocked: () => worldBlocked(),
    // The settlement is a safe zone — see "no combat here" above.
    allowAim: false,
  });
  cleanups.push(() => controls.dispose());

  /**
   * Server reply indices are NOT array positions (S7).
   *
   * `DialogUI.render` calls `onChoose(i)` with the position in the rendered
   * list, but the backend's replies carry explicit `index` values that go
   * non-contiguous as soon as milestone gating removes one — a level-1 player
   * sees indices 0, 1, 3 with 2 hidden. Sending the position would silently fire
   * a different reply than the player clicked. This array is the position ->
   * real-index map for the node currently on screen.
   */
  let replyIndexMap: number[] = [];

  /**
   * Adapts a server `DialogNodeView` to the local shape `DialogUI` renders.
   *
   * `option.to` is set to null throughout: it exists for the sim's local
   * branching, which no longer decides anything here, and `DialogUI.render` only
   * reads `label`. Putting a next-node id in it would imply the client knows
   * where a reply leads — it doesn't, and rule 7 says it must not.
   */
  const toLocalNode = (view: DialogNodeView): DialogNode => {
    replyIndexMap = view.replies.map((r) => r.index);
    return {
      id: view.nodeId,
      npcId: view.npcKey as DialogNode['npcId'],
      text: view.text,
      options: view.replies.map((r) => ({
        label: r.text, to: null, ...(r.kind ? { kind: r.kind } : {}), ...(r.locked ? { locked: r.locked.reason } : {}),
      })),
      ...(view.card ? { card: view.card } : {}),
    };
  };

  /**
   * `nodeId: 'trade'` is a server-authored SENTINEL (see backend/prisma/
   * seed.ts's dialog-tree-marek and schema.prisma's Vendor doc comment):
   * reaching it means "open this NPC's vendor panel" rather than "render this
   * as a normal dialog node". `Vendor.key === Npc.key` by convention, so
   * `view.npcKey` doubles as the vendor key with no extra lookup.
   *
   * MUST be checked at every place a `DialogNodeView` can arrive — not just
   * `showDialogNode` (the initial open). The 'trade' node is reached via a
   * REPLY from 'root', which flows through `onChoose`'s `chooseReply(...)`
   * callback below, a separate code path that never calls `showDialogNode`.
   * A sentinel check placed only in `showDialogNode` would be dead code for
   * the actual entry point — caught in review before this shipped.
   *
   * Closes the dialog via the real `closeDialog()` (not just `dialog.hide()`)
   * so `sim.endDialog()` runs too — skipping it would leave the sim's
   * in-dialog flag set, blocking the "Press E" prompt from ever reappearing
   * behind the vendor panel.
   */
  const maybeOpenVendor = (view: DialogNodeView): boolean => {
    if (view.nodeId !== 'trade') return false;
    closeDialog();
    openVendorUI(view.npcKey);
    return true;
  };

  /**
   * Same sentinel convention as `maybeOpenVendor` above, for the Banker's
   * Gold <-> Cash exchange (`Banker.key === Npc.key`, `nodeId: 'exchange'` —
   * see backend/prisma/content/dialog-trees.ts's `dialog-tree-halvard`).
   * Checked at both call sites for the identical reason `maybeOpenVendor` is.
   */
  const maybeOpenBanker = (view: DialogNodeView): boolean => {
    if (view.nodeId !== 'exchange') return false;
    closeDialog();
    openBankerUI(view.npcKey);
    return true;
  };

  const showDialogNode = (view: DialogNodeView): void => {
    if (maybeOpenVendor(view)) return;
    if (maybeOpenBanker(view)) return;
    const npc = world.npcs[view.npcKey as DialogNode['npcId']];
    if (!npc) return;
    // Portrait/name/role stay client-side: Vite content-hashes the bundled
    // portrait imports, so the DB's `portrait` path cannot address them.
    // The body model lives on the placed world OBJECT (kind 'npc'), not on
    // `world.npcs` — resolve it here so the dialog's 3D preview shows the
    // NPC's actual rigged body instead of the blocky placeholder.
    const bodyModelUrl = world.objects.find((o) => o.kind === 'npc' && o.npcId === npc.id)?.modelUrl;
    // A conversation is a window too, and it is the one that can be reached
    // WITHOUT the keyboard gates: the Survivor Record does not block world
    // input (by design), so the player can be standing at an NPC with the bag
    // open and press E. The dialog is z-index 10 and would have opened under
    // the Record's scrim.
    closeOtherModals('dialog');
    dialog.show(npc, toLocalNode(view), {
      onChoose: (pos) => {
        const realIndex = replyIndexMap[pos];
        if (realIndex === undefined) return;
        void chooseReply(realIndex).then((next) => {
          if (!next) { closeDialog(); return; }
          if (maybeOpenVendor(next)) return;
          if (maybeOpenBanker(next)) return;
          dialog.render(toLocalNode(next));
        });
      },
      onClose: () => closeDialog(),
    }, bodyModelUrl);
  };

  const closeDialog = (): void => {
    closeNpcDialog();
    sim.endDialog();
    dialog.hide();
    replyIndexMap = [];
    // A conversation is the PRIMARY way a story quest is accepted, turned in
    // or claimed (`questAssignId` / `quest.turnIn:` / `questClaimId` outcomes
    // in backend/prisma/content/dialog-trees.ts). None of those come back
    // through this client as anything it could patch into the board, so the
    // board is refetched whenever a conversation ends — otherwise the `!` over
    // an NPC's head would keep offering a job the player just took, until
    // something else happened to reload.
    void loadQuestBoard();
    // And the DEED can change hands in a conversation too. `overseer-your-plot`
    // is turned in through dialog and `HomeService.claimFromStory` writes
    // `homeOwned` server-side, but nothing pushes that back: `home.module.ts`
    // says plainly there is no `GameGateway` subscriber for its `home-updated`
    // event, `loadHome()` otherwise runs only at mount, and `openHomeUI` now
    // REFUSES while unowned so it cannot bootstrap itself either.
    //
    // Without this line the player finishes the Overseer's quest and the house
    // stays boarded up — no prompt, H disabled — until they reload or happen to
    // open the black market. Janeks' ask was "the key/quest unlocks it": the key
    // half already worked (the purchase writes the store), the quest half did
    // not.
    void loadHome();
  };

  const interact = (): void => {
    if (worldBlocked()) return;
    // The sim still owns proximity (the GDD assigns it to Base View) and the
    // "am I in a dialog" flag that suppresses the prompt — but the tree itself
    // now comes from the server, so its returned node is discarded.
    const local = sim.tryInteract();
    if (!local) return;
    // Your own house is an interactable body, not a person. Item 208's entry
    // point is pressing E on it ("Go to home, press on it"), and without this
    // branch that press ran `openNpcDialog('home')` — which finds no dialog
    // tree, returns nothing and does NOTHING AT ALL: no panel, no error, no
    // sign anything happened.
    if (local.npcId === HOME_NPC_ID) {
      // `setInteractableSuppressed` above already stops an unowned house ever
      // reaching here. Re-checked anyway: this branch is one line from
      // `openHomeUI()` and a reader should be able to see the rule without
      // tracing a subscription forty lines up.
      if (homeDeedHeld) openHomeUI();
      return;
    }
    void openNpcDialog(local.npcId).then((view) => {
      if (view) showDialogNode(view);
      else closeDialog(); // authoring error / request failed — don't strand the sim in-dialog
    });
  };

  // DEV inspection hook (same gate as arena-view's window.__duel): lets a probe
  // teleport next to an NPC and press E without hunting for it on screen.
  if (import.meta.env.DEV) {
    (window as unknown as Record<string, unknown>).__settlement = { sim, world, interact };
  }

  // ---- listeners ----
  const onKeyDown = (e: KeyboardEvent): void => {
    // Crouch ('c') and gait (CapsLock). Runs BEFORE the dialog's early-return
    // on purpose: `controls.handleKeyDown` gates the crouch toggle on
    // `isInputBlocked` itself, but its CapsLock re-sync must stay reachable
    // while a panel is open — a dialog is exactly when a CapsLock press gets
    // missed, and returning early here would leave the HUD's gait indicator
    // lying until the panel closed and another key was pressed.
    // Typing in the chat box must not also drive the player. This handler is
    // on `window`, so without the guard every letter of "wts scrap" is a
    // movement key and the survivor walks off across the settlement while you
    // write. It sits at the very top because `controls.handleKeyDown` (crouch,
    // gait) and the action-bar shortcuts below all read the same event.
    if (isTypingTarget(e.target)) return;

    // Escape closes the Survivor Record. Janeks: *"on ESC press - close the
    // current modal."* Every other window in the game carries its own Escape
    // handler; the Record is the one that never did, because it is the one
    // surface that does not block world input (see its registration above) and
    // so was never on the list of things a key had to get out of.
    //
    // Handled HERE rather than in `paperdoll.ts` for the same reason its panel
    // registration lives here: this file owns the Record, and the paperdoll
    // module is a renderer that several surfaces (the arena confirm, the
    // vendor) draw through. Ahead of `controls.handleKeyDown` so it cannot be
    // swallowed, and it returns, because one Escape should close one thing.
    if (e.key === 'Escape' && paperdoll.open) {
      e.preventDefault();
      paperdoll.hide();
      return;
    }

    controls.handleKeyDown(e);
    if (dialog.handleKey(e.key)) {
      e.preventDefault();
      return;
    }
    // Space is swallowed, and this is NOT leftover combat plumbing — its
    // default action scrolls the page, which in a full-viewport 3D view reads
    // as the camera breaking. It used to ride along on the Space-fires guard
    // (`isFireKey`) that the "no combat here" removal took out; putting it
    // back cost one line and a real bug otherwise.
    //
    // Deliberately `e.code === 'Space'` rather than `isFireKey`: that helper
    // is gated on the `fireWithSpace` SETTING, so routing the scroll guard
    // through it would let a player who turns firing off re-enable page
    // scrolling under the game. The guard is about the browser, not about
    // firing, so it must not depend on a combat preference — least of all
    // here, where there is no firing to prefer.
    if (e.code === 'Space') {
      e.preventDefault();
      return;
    }
    const pressed = keyName(e);
    keys.add(pressed);
    if (pressed === 'e') interact();
    // Every window shortcut, from the ONE list the action bar renders from —
    // so a key and its button always agree. Replaces four hand-copied guard
    // chains; see `actionItems` above for each window's own gate.
    const action = actionItems.find((a) => a.key === pressed);
    if (action && action.enabled()) action.toggle();
    // R still opens the map. It was the old raid panel's binding and is kept
    // as muscle memory (it predates M) — the request added M as a button, it
    // did not ask for R to stop working. Open-only, matching what R did
    // before: it never toggled.
    if (pressed === 'r') {
      const map = actionItems.find((a) => a.id === 'map');
      if (map?.enabled() && !isWorldMapUIOpen()) openWorldMapUI();
    }
  };
  const onKeyUp = (e: KeyboardEvent): void => {
    keys.delete(keyName(e));
    // The other half of the CapsLock gait re-sync. macOS fires keydown when
    // the lock engages and only keyup when it releases, so without this the
    // walk→run transition is invisible until the next keypress — see
    // `syncGait` in player-controls.ts. Not gated on `isInputBlocked` for the
    // same reason the keydown read is not.
    controls.handleKeyUp(e);
  };

  const onPointerDown = (e: PointerEvent): void => {
    // Only the canvas itself should start a camera-drag/aim capture.
    // `container` also wraps the dialog/paperdoll DOM overlays — capturing
    // the pointer unconditionally here redirects their *click* events (not
    // just pointer events; per spec, pointer capture redirects the
    // associated mouse events too) to `container`, so buttons underneath
    // (close, dialog options) silently stop receiving clicks. Applies to
    // every button below, not just LMB drag.
    if (e.target !== renderer.domElement) return;
    // RMB (aim entry) and cursor tracking are handled by `controls`, which
    // has its own listener on `container`; this handler only needs LMB.
    if (e.button !== 0) return;
    dragging = true;
    dragMoved = 0;
    lastPointerX = e.clientX;
    container.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: PointerEvent): void => {
    hoverId = picker.pick(e.clientX, e.clientY, container, cam.camera, sceneHandles.pickables);
    if (dragging && !worldBlocked()) {
      const dx = e.clientX - lastPointerX;
      lastPointerX = e.clientX;
      dragMoved += Math.abs(dx);
      cam.rotate(dx);
    }
  };
  const onPointerUp = (e: PointerEvent): void => {
    if (dragging && dragMoved < 5) {
      // treated as a click: talk if the clicked object is the one we're near
      const near = sim.getSnapshot().nearestInteractable;
      if (hoverId && near && near.objectId === hoverId) interact();
    }
    dragging = false;
    if (container.hasPointerCapture(e.pointerId)) container.releasePointerCapture(e.pointerId);
  };
  const onWheel = (e: WheelEvent): void => {
    // Modal panels are mounted as children of `container`, so their wheel
    // events bubble here too — an unconditional preventDefault() would kill
    // the scrollable item grids' native scroll along with the camera zoom.
    // Same guard as every other input handler in this file.
    if (worldBlocked()) return;
    e.preventDefault();
    cam.zoom(Math.sign(e.deltaY));
  };

  container.addEventListener('pointerdown', onPointerDown);
  container.addEventListener('pointermove', onPointerMove);
  container.addEventListener('pointerup', onPointerUp);
  container.addEventListener('wheel', onWheel, { passive: false });
  window.addEventListener('keydown', onKeyDown);
  window.addEventListener('keyup', onKeyUp);
  // Held keys are dropped when their keyup can be lost — see key-input.ts.
  cleanups.push(trackHeldKeys(keys));
  cleanups.push(
    () => container.removeEventListener('pointerdown', onPointerDown),
    () => container.removeEventListener('pointermove', onPointerMove),
    () => container.removeEventListener('pointerup', onPointerUp),
    () => container.removeEventListener('wheel', onWheel),
    () => window.removeEventListener('keydown', onKeyDown),
    () => window.removeEventListener('keyup', onKeyUp),
  );

  // ---- resize ----
  const resize = (): void => {
    const w = container.clientWidth || 1;
    const h = container.clientHeight || 1;
    renderer.setSize(w, h, false);
    cam.setAspect(w / h);
  };
  const ro = new ResizeObserver(resize);
  ro.observe(container);
  cleanups.push(() => ro.disconnect());
  resize();

  // ---- frame loop ----
  let raf = 0;
  let last = performance.now();
  let fps = 0;
  let fpsAccum = 0;
  let fpsFrames = 0;

  const frame = (now: number): void => {
    raf = requestAnimationFrame(frame);
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;

    // Ground-plane aim raycast, recomputed every frame while aiming —
    // including frames with zero movement input, since tracking the cursor
    // while stationary is the whole point (§3.7). Runs before this frame's
    // sim.update(), so it necessarily uses the previous frame's position.
    controls.updateAim(sim.getSnapshot().player.position);

    // Held keys stay in `keys` while a window is open — releasing one is still
    // tracked — but they are not READ: the survivor stands still behind a menu
    // and walks again the moment it closes if the key is still down.
    const blocked = worldBlocked();
    const input = {
      moveX: blocked ? 0 : (keys.has('d') || keys.has('arrowright') ? 1 : 0) - (keys.has('a') || keys.has('arrowleft') ? 1 : 0),
      moveZ: blocked ? 0 : (keys.has('w') || keys.has('arrowup') ? 1 : 0) - (keys.has('s') || keys.has('arrowdown') ? 1 : 0),
      aimYaw: controls.simAimYaw,
      gait: controls.gait,
      crouched: controls.crouched,
    };
    sim.update(dt, input, cam.yawValue);

    const snap = sim.getSnapshot();
    sceneHandles.player.position.set(snap.player.position.x, 0, snap.player.position.z);
    sceneHandles.player.rotation.y = snap.player.facing;
    playerShadow.position.set(snap.player.position.x, playerShadow.position.y, snap.player.position.z);
    playerShadow.rotation.y = snap.player.facing;
    cam.update(snap.player.position);

    // Reads the same controls state the input object above was just built
    // from, plus dialog.isOpen (already read elsewhere in this loop) — no new
    // state, just feeding what's already tracked into the animator
    // (character-animation-runtime.md Phase 1).
    playerAnimator.update(dt, {
      moving: snap.player.moving,
      gait: controls.gait,
      crouched: controls.crouched,
      aiming: controls.aiming,
      dialogOpen: dialog.isOpen,
    });
    // Ambient NPC animation. The talking override goes to whichever NPC the
    // player actually has open — `snap.interaction` carries the npcId in its
    // 'dialog' shape, so no second source of "who am I talking to" is needed.
    npcAnimators.update(dt, snap.interaction.kind === 'dialog' ? snap.interaction.npcId : null);
    for (const shot of oneShots) {
      const t = (oneShotTimers.get(shot.objectId) ?? 0) + dt;
      if (t >= shot.everySec) {
        oneShotTimers.set(shot.objectId, 0);
        npcAnimators.playOneShot(shot.objectId, shot.kind);
      } else {
        oneShotTimers.set(shot.objectId, t);
      }
    }
    npcAnimators.syncActors(snap.actors);

    // hover highlight + settlement ambience + interactable cursor (before render)
    const inRange = !!snap.nearestInteractable && snap.nearestInteractable.objectId === hoverId;
    sceneHandles.setHovered(hoverId, inRange);
    sceneHandles.updateAmbience(dt, reducedMotionMql.matches);
    sceneHandles.updateGrass(dt, cam.yawValue);
    // Authored fire/smoke (`WorldData.spriteSpawns`) — the only source of
    // either since the model-keyed auto-attach was removed. Owned by the scene
    // rather than by this module, because the editor rebuilds it live through
    // `setSpriteSpawns`, so the group can be a different one frame to frame.
    // Without this call a placed fire renders as a frozen first frame, which
    // looks plausible in a screenshot.
    sceneHandles.spriteSpawns.update(dt);
    // The listener rides the CAMERA, not the player: the camera is where the
    // view is from, so a fire the player has walked past but the camera still
    // faces should still sound placed correctly. `getWorldDirection` gives the
    // forward vector the panners resolve against.
    if (fireAmbience) {
      cam.camera.getWorldDirection(listenerForward);
      fireAmbience.updateListener(cam.camera.position, listenerForward);
    }

    // Markers follow the LIVE actor poses, not the authored anchors — a
    // routine NPC walks out from under a pinned glyph (same reason the talk
    // trigger reads `livePosition`). `now / 1000` rather than an accumulated
    // dt so the bob phase can't drift across a tab-throttled frame.
    questMarkers.update(now / 1000, snap.actors);
    // Same live poses as the markers, for the same reason — plus the player's
    // own position, which the tags fade every other name against.
    nameTags.update(snap.player.position, snap.actors);
    healNumbers.update(dt);
    updateFogDrift(dt);
    renderer.domElement.classList.toggle('bv-hover-interactable', hoverId !== null && interactableIds.has(hoverId));

    if (!inRaid) renderer.render(sceneHandles.scene, cam.camera);

    // interaction prompt (rebuild DOM only when the target NPC changes)
    // `npcId` is optional on 'near' now (S17 added lootObjectId as the
    // alternative) — the settlement world has no loot objects, so this
    // branch is unreached in practice, but the guard keeps it correct.
    if (!worldBlocked() && snap.interaction.kind === 'near' && snap.interaction.npcId) {
      if (snap.interaction.npcId !== promptNpcId) {
        promptNpcId = snap.interaction.npcId;
        const npc = world.npcs[promptNpcId];
        // The house is an interactable BODY, not a person — it carries an
        // `npcId` only because that is how the map marks something usable. It
        // read "Press E to talk to Your Home" until this branch existed, and
        // it is only ever reachable while the deed is held (an unowned house
        // is suppressed out of `nearestInteractable` entirely).
        prompt.innerHTML = promptNpcId === HOME_NPC_ID
          ? 'Press <b>E</b> to step inside'
          : `Press <b>E</b> to talk to ${npc?.name ?? 'someone'}`;
      }
      prompt.hidden = false;
    } else {
      promptNpcId = null;
      prompt.hidden = true;
    }

    // fps (smoothed, ~5 Hz refresh)
    fpsAccum += dt;
    fpsFrames += 1;
    if (fpsAccum >= 0.2) {
      fps = fpsFrames / fpsAccum;
      fpsAccum = 0;
      fpsFrames = 0;
    }
    debug?.update(fps, hoverId, snap.player.position, {
      distance: cam.distanceValue, min: cam.minDistance, max: cam.maxDistance,
    });
    hud.update(now, controls.gait, controls.crouched);
    // Pressed/disabled state for the window buttons. Diffs internally —
    // a frame where nothing opened or closed writes no DOM at all.
    actionBar.update();

    // Arena timers. Every number it shows is DERIVED from the last server
    // push plus elapsed wall time, so it has to be repainted from the frame
    // loop rather than counted down on an interval — a backgrounded tab gets
    // its intervals throttled and would come back showing the wrong time.
    // Writes only when a rendered string actually changes.
    arenaUI.update();

    // Yielded to any panel that overlaps it rather than competing for the
    // corner; `update()` no-ops entirely while hidden.
    minimap.el.hidden = minimapBlocked() || isArenaModalOpen() || inRaid;
    // The gait indicator sits beside the minimap, so it hides with it —
    // derived from the same value rather than a second copy of that predicate,
    // which is exactly how the action bar's five hand-copied gates drifted.
    hud.gaitEl.hidden = minimap.el.hidden;
    // Janeks: *"Menu must be behind the overlay."* The player card and the
    // action bar used to sit at z-index 1002, ABOVE every panel scrim, so an
    // open window was dimmed AROUND a fully lit menu whose buttons were still
    // clickable — which is also how a second window got opened on top of the
    // first. The class drops them under the scrim; the arena pending badge
    // keeps its own layer above it (styles/hud.css.ts). Toggled per frame, from
    // the same place the minimap's predicate is read, rather than from every
    // open/close site.
    const underPanel = modalOpen() || isArenaModalOpen();
    hud.el.classList.toggle('bv-hud-under-panel', underPanel);
    actionBar.el.classList.toggle('bv-ab-dock-under-panel', underPanel);
    minimap.update(now, snap.player.position, snap.player.facing, cam.yawValue);

    // raid polling (self-throttled to pollIntervalMs; sync, so an idle frame
    // allocates nothing)
    updateRaidPolling();
  };
  raf = requestAnimationFrame(frame);

  // ---- teardown ----
  return () => {
    torndown = true;
    cancelAnimationFrame(raf);
    for (const fn of cleanups) fn();
    dialog.dispose();
    debug?.dispose();
    hud.dispose();
    minimap.dispose();
    paperdoll.dispose();
    disposeWorldMapUI();
    worldMapUI.remove();
    disposeVendorUI();
    vendorUI.remove();
    disposeCurrencyShopUI();
    currencyShopUI.remove();
    disposeBankerUI();
    bankerUI.remove();
    disposeQuestUI();
    questUI.remove();
    disposeShopUI();
    shopUI.remove();
    disposeHomeUI();
    homeUI.remove();
    prompt.remove();
    sceneHandles.dispose();
    renderer.dispose();
    renderer.forceContextLoss();
    renderer.domElement.remove();
    removeStyles();
    container.remove();
  };
}

/**
 * Chest height on this chibi rig, metres. Named for the muzzle because that
 * is what it was measured against — it now only places the floating heal
 * numbers, which want the same "reads as coming from the torso" height. The
 * forward companion (`MUZZLE_FORWARD`) went with the muzzle flash; see the
 * "no combat here" note above.
 */
const MUZZLE_HEIGHT = 1.15;

function aspectOf(el: HTMLElement): number {
  const w = el.clientWidth || 1;
  const h = el.clientHeight || 1;
  return w / h;
}

